import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Wrench, DollarSign, Percent, Loader2, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { apiClient } from "@/lib/api";
import { canEditMechanicPrices } from "@/lib/roles";
import { PageLoader, Card } from "@/components/shared/ui";

const ACCENT = "#059669";
const HOUSE_TYPES = ["C", "B", "A"];

export default function MechanicPricing() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingType, setSavingType] = useState("");
  const [savedType, setSavedType] = useState("");
  const [error, setError] = useState("");

  const allowed = canEditMechanicPrices(user);

  useEffect(() => {
    if (!allowed) return;
    apiClient
      .get("/mechanic/config")
      .then((cfg) => {
        setConfig(cfg);
        setDrafts(Object.fromEntries(Object.entries(cfg.base_prices || {}).map(([k, v]) => [k, String(v)])));
      })
      .catch((e) => setError(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [allowed]);

  if (!allowed) return <Navigate to="/" replace />;
  if (loading) return <PageLoader />;

  const save = async (ht) => {
    const val = parseFloat(drafts[ht]);
    if (!val || val <= 0) {
      setError(t("enter_base_price"));
      return;
    }
    setSavingType(ht);
    setSavedType("");
    setError("");
    try {
      const res = await apiClient.put("/mechanic/base-prices", { house_type: ht, base_price: val });
      setConfig((c) => ({ ...c, base_prices: { ...c.base_prices, [ht]: res.base_price } }));
      setSavedType(ht);
      setTimeout(() => setSavedType(""), 2000);
    } catch (e) {
      setError(e.message || "Failed to update price");
    } finally {
      setSavingType("");
    }
  };

  return (
    <div className="mx-auto max-w-3xl pb-10">
      <div className="mb-5 flex items-center gap-3 rounded-2xl border p-5" style={{ backgroundColor: `${ACCENT}0d`, borderColor: `${ACCENT}33` }}>
        <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${ACCENT}20` }}>
          <Wrench className="h-6 w-6" style={{ color: ACCENT }} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">{t("mechanic_pricing")}</h1>
          <p className="text-sm text-slate-500">{t("base_prices_title")}</p>
        </div>
      </div>

      {error ? <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div> : null}

      <Card className="mb-5">
        <div className="mb-4 flex items-center gap-2">
          <DollarSign className="h-5 w-5" style={{ color: ACCENT }} />
          <h3 className="text-base font-semibold text-slate-900">{t("base_prices_title")}</h3>
        </div>
        <div className="space-y-3">
          {HOUSE_TYPES.map((ht) => (
            <div key={ht} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 p-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg text-base font-bold text-white" style={{ backgroundColor: ACCENT }}>{ht}</span>
              <span className="flex-1 text-sm font-semibold text-slate-700">{t("house_type")} {ht}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-slate-400">$</span>
                <input
                  data-testid={`price-input-${ht}`}
                  className="h-10 w-32 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-emerald-500"
                  value={drafts[ht] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [ht]: e.target.value.replace(/[^0-9.]/g, "") }))}
                  inputMode="decimal"
                />
              </div>
              <button
                data-testid={`save-price-${ht}`}
                onClick={() => save(ht)}
                disabled={savingType === ht}
                className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                style={{ backgroundColor: ACCENT }}
              >
                {savingType === ht ? <Loader2 className="h-4 w-4 animate-spin" /> : savedType === ht ? <Check className="h-4 w-4" /> : null}
                {savedType === ht ? t("base_price_updated") : t("update_price")}
              </button>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="mb-4 flex items-center gap-2">
          <Percent className="h-5 w-5" style={{ color: ACCENT }} />
          <h3 className="text-base font-semibold text-slate-900">{t("work_items_percentages")}</h3>
        </div>
        <div className="space-y-1.5">
          {(config?.work_items || []).map((it, idx) => (
            <div key={it.item_code} className={`flex items-center gap-3 rounded-lg p-2.5 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">{idx + 1}</span>
              <span className="flex-1 text-sm text-slate-800">{it.name_ku}</span>
              <span className="rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">{it.percentage}%</span>
            </div>
          ))}
          <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-3 text-sm">
            <span className="font-semibold text-slate-500">{t("total_word")}</span>
            <span className="font-bold text-slate-900">
              {(config?.work_items || []).reduce((s, it) => s + Number(it.percentage || 0), 0)}%
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}
